import { apiClient } from "./apiClient";

export interface BusinessItem {
  _id: string;
  id: string;
  slug: string;
  businessName: string;
  name: string;
  category: string;
  businessType?: { id?: string; name?: string } | string;
  logo?: string;
  coverImage?: string;
  image: string;
  rating: number;
  reviewCount: number;
  serviceCategories?: string[];
  coverageHexCount?: number;
  offerCount?: number;
  offersCount?: string;
  city?: string;
  location?: string;
  address?: string;
  distance?: string | null;
  distanceKm?: number | null;
  phone?: string;
  featuredOffer?: string;
}

export interface ExploreResponse {
  items: BusinessItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const RESTAURANT_CAT_MAP: Record<string, string> = {
  cafe: "bakery",
  biryani: "biryani",
  kerala: "food",
  "fast-food": "fast food",
  desserts: "bakery",
  "fine-dining": "restaurant",
};

const SERVICE_CAT_MAP: Record<string, string> = {
  cleaning: "home",
  beauty: "salon",
  electrical: "repair",
  repair: "repair",
  pest: "pest",
  education: "coaching",
  construction: "construction",
};

export async function getRestaurants(params?: {
  location?: string;
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
  lat?: number | null;
  lng?: number | null;
  sort?: string;
}): Promise<ExploreResponse> {
  const query: Record<string, string> = { type: "restaurant" };
  const isAll =
    !params?.location ||
    params.location === "All" ||
    params.location.toLowerCase() === "all locations";

  if (!isAll) {
    if (params?.location && params.location !== "Current Location") {
      query.location = params.location;
    }
    if (params?.lat != null) query.lat = String(params.lat);
    if (params?.lng != null) query.lng = String(params.lng);
    if (params?.sort) query.sort = params.sort;
  }
  if (params?.category && params.category !== "all") {
    query.category = RESTAURANT_CAT_MAP[params.category] || params.category;
  }
  if (params?.search) query.search = params.search;
  if (params?.page) query.page = String(params.page);
  if (params?.limit) query.limit = String(params.limit);

  try {
    const res = await apiClient.get<any>("/loyalty/public/restaurants", query);
    if (res.success && res.data) {
      const items = res.data.items || (Array.isArray(res.data) ? res.data : []);
      const pagination = res.data.pagination || {
        page: params?.page || 1,
        limit: params?.limit || 12,
        total: items.length,
        pages: 1,
      };
      return { items, pagination };
    }
  } catch (err) {
    console.warn("getRestaurants API error:", err);
  }

  // Return empty if no restaurants found from API
  return {
    items: [],
    pagination: { page: 1, limit: 12, total: 0, pages: 1 },
  };
}

export async function getServices(params?: {
  location?: string;
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
  lat?: number | null;
  lng?: number | null;
  sort?: string;
}): Promise<ExploreResponse> {
  const query: Record<string, string> = { type: "service" };
  const isAll =
    !params?.location ||
    params.location === "All" ||
    params.location.toLowerCase() === "all locations";

  if (!isAll) {
    if (params?.location && params.location !== "Current Location") {
      query.location = params.location;
    }
    if (params?.lat != null) query.lat = String(params.lat);
    if (params?.lng != null) query.lng = String(params.lng);
    if (params?.sort) query.sort = params.sort;
  }
  if (params?.category && params.category !== "all") {
    query.category = SERVICE_CAT_MAP[params.category] || params.category;
  }
  if (params?.search) query.search = params.search;
  if (params?.page) query.page = String(params.page);
  if (params?.limit) query.limit = String(params.limit);

  try {
    const res = await apiClient.get<any>("/loyalty/public/services", query);
    if (res.success && res.data) {
      const items = res.data.items || (Array.isArray(res.data) ? res.data : []);
      const pagination = res.data.pagination || {
        page: params?.page || 1,
        limit: params?.limit || 12,
        total: items.length,
        pages: 1,
      };
      return { items, pagination };
    }
  } catch (err) {
    console.warn("getServices API error:", err);
  }

  // Return empty if no services found from API
  return {
    items: [],
    pagination: { page: 1, limit: 12, total: 0, pages: 1 },
  };
}
